import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTier } from '../context/TierContext';
import { Edit2, Mail, Phone, Share2, MapPin, Save, Star, Award, Briefcase, Building2, Calendar, Linkedin, Lock, Download, FileText, CheckCircle, Clock, DollarSign, Check, X, Plus, Trash2, Camera } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { profileApi, analyticsApi, authApi, uploadsApi } from '../services/api';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { getProfilePictureOrInitials } from '../utils/profilePicture';
import { getTierColor } from '../utils/tierColors';
import CompanyLogoEditor from './CompanyLogoEditor';
import { toast } from 'sonner';
import ProfilePictureUploadDialog from './ProfilePictureUploadDialog';

const SPECIALIZATION_OPTIONS = [
  'Air Freight',
  'Ocean Freight',
  'Project Cargo',
  'Customs Clearance',
  'Warehousing',
  'E-commerce Fulfilment',
  'LCL',
  'Breakbulk',
  '3PL Solutions',
  'FCL',
  'Cross Trade',
  'Courier',
  'Road Freight',
  'Rail Freight',
  'Dangerous Goods',
  'Reefer Cargo',
  'Buyer Consolidation',
  'Distribution',
  'Last Mile Delivery',
  'White Glove Logistics',
];

const Profile = () => {
  const { pointsSystemEnabled } = useTier();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [stats, setStats] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [showProfilePictureDialog, setShowProfilePictureDialog] = useState(false);
  const [downloadingCertificate, setDownloadingCertificate] = useState(false);
  const [companyRegistration, setCompanyRegistration] = useState(null);
  const logoInputRef = useRef(null);
  const [downloadingBadge, setDownloadingBadge] = useState(false);
  const [specializationDraft, setSpecializationDraft] = useState('');
  const [isAddingSpecialization, setIsAddingSpecialization] = useState(false);
  const [bankDetails, setBankDetails] = useState([]);
  const [canManageBankDetails, setCanManageBankDetails] = useState(false);
  const [bankDetailsLoading, setBankDetailsLoading] = useState(false);
  const [bankDetailsSaving, setBankDetailsSaving] = useState(false);
  const [bankForm, setBankForm] = useState({
    detail_id: '',
    account_name: '',
    bank_name: '',
    account_number: '',
    iban: '',
    swift_code: '',
    bank_address: '',
    currency: '',
    country: '',
    label: '',
    is_default: false,
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const { member, refreshUser } = useAuth();
  const navigate = useNavigate();
  const currentMemberId = member?.member_id;
  const canManageCompanyDocuments =
    member?.has_admin_rights === true || profileData?.is_company_admin === true;
  const canManageSpecializations =
    member?.has_admin_rights === true || profileData?.is_company_admin === true;

  useEffect(() => {
    fetchData();
    fetchCompanyRegistration();
  }, [currentMemberId]);

  useEffect(() => {
    if (currentMemberId) {
      fetchCompanyBankDetails();
    }
  }, [currentMemberId]);

  const hasUnsavedChanges = useMemo(() => {
    if (!profileData || !originalData) return false;
    return JSON.stringify(profileData) !== JSON.stringify(originalData);
  }, [originalData, profileData]);

  const effectiveMemberRating = useMemo(() => {
    return profileData?.member_rating || stats?.rating || 3;
  }, [profileData?.member_rating, stats?.rating]);

  const handleShareClick = () => {
    const shareUrl = `${window.location.origin}/share-social`;
    window.open(shareUrl, '_blank', 'noopener,noreferrer');
  };

  const specializationSuggestions = useMemo(() => {
    const existingSpecializations = new Set(
      (profileData?.specializations || []).map((spec) => String(spec).trim().toLowerCase())
    );

    return SPECIALIZATION_OPTIONS.filter(
      (option) => !existingSpecializations.has(option.trim().toLowerCase())
    );
  }, [profileData?.specializations]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!hasUnsavedChanges) return;
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const fetchCompanyRegistration = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${API_URL}/api/company-registration/my-registration`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.exists) {
        setCompanyRegistration(response.data);
      }
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error('Failed to fetch company registration:', err);
      }
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const profileRes = await profileApi.getProfile(currentMemberId);
      setProfileData(profileRes.data);
      setOriginalData(profileRes.data);
      setIsAddingSpecialization(false);
      setSpecializationDraft('');

      // Always fetch overview for own profile (backend returns own stats without analytics tier)
      try {
        const statsRes = await analyticsApi.getOverview(currentMemberId);
        setStats(statsRes.data.overview);
      } catch (analyticsError) {
        if (analyticsError?.response?.status === 403) {
          setStats(null);
        } else {
          throw analyticsError;
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyBankDetails = async () => {
    if (!currentMemberId) return;
    try {
      setBankDetailsLoading(true);
      const res = await profileApi.getCompanyBankDetails(currentMemberId);
      setBankDetails(res?.data?.details || []);
      setCanManageBankDetails(Boolean(res?.data?.can_manage_bank_details));
    } catch (error) {
      console.error('Error fetching company bank details:', error);
      setBankDetails([]);
      setCanManageBankDetails(false);
    } finally {
      setBankDetailsLoading(false);
    }
  };

  const resetBankForm = () => {
    setBankForm({
      detail_id: '',
      account_name: '',
      bank_name: '',
      account_number: '',
      iban: '',
      swift_code: '',
      bank_address: '',
      currency: '',
      country: '',
      label: '',
      is_default: false,
    });
  };

  const startEditBankDetail = (detail) => {
    setBankForm({
      detail_id: detail.detail_id || '',
      account_name: detail.account_name || '',
      bank_name: detail.bank_name || '',
      account_number: detail.account_number || '',
      iban: detail.iban || '',
      swift_code: detail.swift_code || '',
      bank_address: detail.bank_address || '',
      currency: detail.currency || '',
      country: detail.country || '',
      label: detail.label || '',
      is_default: !!detail.is_default,
    });
  };

  const handleSaveBankDetail = async () => {
    if (!canManageBankDetails) return;
    if (!bankForm.account_name || !bankForm.bank_name || !bankForm.account_number) {
      toast.error('Account name, bank name, and account number are required');
      return;
    }
    try {
      setBankDetailsSaving(true);
      const payload = {
        account_name: bankForm.account_name,
        bank_name: bankForm.bank_name,
        account_number: bankForm.account_number,
        iban: bankForm.iban || '',
        swift_code: bankForm.swift_code || '',
        bank_address: bankForm.bank_address || '',
        currency: bankForm.currency || '',
        country: bankForm.country || '',
        label: bankForm.label || '',
        is_default: !!bankForm.is_default,
      };
      if (bankForm.detail_id) {
        await profileApi.updateCompanyBankDetail(currentMemberId, bankForm.detail_id, payload);
        toast.success('Bank detail updated');
      } else {
        await profileApi.createCompanyBankDetail(currentMemberId, payload);
        toast.success('Bank detail added');
      }
      resetBankForm();
      fetchCompanyBankDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save bank details');
    } finally {
      setBankDetailsSaving(false);
    }
  };

  const handleDeleteBankDetail = async (detailId) => {
    if (!canManageBankDetails || !detailId) return;
    if (!window.confirm('Delete this bank account detail?')) return;
    try {
      await profileApi.deleteCompanyBankDetail(currentMemberId, detailId);
      toast.success('Bank detail deleted');
      if (bankForm.detail_id === detailId) {
        resetBankForm();
      }
      fetchCompanyBankDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete bank details');
    }
  };

  const handleSave = async () => {
    try {
      await profileApi.updateProfile(currentMemberId, profileData);
      toast.success('Profile updated successfully!');
      setOriginalData(profileData);
      setIsEditing(false);
      setIsAddingSpecialization(false);
      setSpecializationDraft('');
      refreshUser?.(); // Refresh auth context so Sidebar/Header show updated logo
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handleCancel = () => {
    setProfileData(originalData);
    setIsEditing(false);
    setIsAddingSpecialization(false);
    setSpecializationDraft('');
  };

  const handleStartSpecializationAdd = () => {
    if (!canManageSpecializations) return;
    setIsAddingSpecialization(true);
    setSpecializationDraft('');
  };

  const handleDiscardSpecializationAdd = () => {
    setIsAddingSpecialization(false);
    setSpecializationDraft('');
  };

  const handleConfirmSpecializationAdd = () => {
    if (!canManageSpecializations) return;
    const trimmed = specializationDraft.trim();
    if (!trimmed) {
      toast.error('Enter a specialization');
      return;
    }
    const existingSpecializations = profileData?.specializations || [];
    const duplicateExists = existingSpecializations.some(
      (spec) => String(spec).trim().toLowerCase() === trimmed.toLowerCase()
    );
    if (duplicateExists) {
      toast.error('This specialization already exists');
      return;
    }

    setProfileData({
      ...profileData,
      specializations: [...existingSpecializations, trimmed],
    });
    setIsAddingSpecialization(false);
    setSpecializationDraft('');
    toast.success('Specialization added locally. Click Save Changes to apply.');
  };

  const handleRemoveSpecialization = (valueToRemove) => {
    if (!canManageSpecializations) return;
    const nextSpecializations = (profileData?.specializations || []).filter(
      (spec) => String(spec).trim().toLowerCase() !== String(valueToRemove).trim().toLowerCase()
    );
    setProfileData({
      ...profileData,
      specializations: nextSpecializations,
    });
  };

  const handleChangePasswordDialogClose = (open) => {
    if (!open) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    }
    setShowChangePasswordDialog(open);
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    if (!canManageCompanyDocuments) {
      toast.error('Company documents and logo can only be managed by the company admin.');
      return;
    }
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error('Logo must be less than 5MB');
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
      toast.error('Please upload a valid image (JPEG, PNG, GIF, or WebP)');
      return;
    }
    setUploadingLogo(true);
    try {
      const res = await uploadsApi.uploadFile(file, 'company_logo', null, currentMemberId);
      const fileUrl = res.data?.file?.url ?? res.data?.file_url ?? (res.data?.file?.filename && `/api/uploads/files/company_logo/${res.data.file.filename}`);
      if (fileUrl) {
        setProfileData({ ...profileData, company_logo: fileUrl });
        toast.success('Logo uploaded. Click Save Changes to apply.');
      } else {
        toast.error('Upload succeeded but no URL returned');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleChangePasswordSubmit = async () => {
    const passwordHasMinLength = newPassword.length >= 8;
    const passwordHasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);

    if (!passwordHasMinLength || !passwordHasSpecialChar) {
      toast.error('New password must be at least 8 characters and include at least one special character');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New password and confirmation do not match');
      return;
    }
    try {
      setChangingPassword(true);
      await authApi.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success('Password changed successfully! A confirmation email has been sent.');
      handleChangePasswordDialogClose(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  // Certificate and badge downloads are member-facing assets. Team members may not
  // manage registration documents, but they should still be able to download them
  // once the company/member is approved.
  const isCompanyApproved = companyRegistration?.status?.toLowerCase() === 'approved';
  const isActiveMember = profileData?.status === 'active';
  const isRegisteredUser = (profileData?.tier || '').toLowerCase() === 'registered user';
  const hasMemberDownloads = (isCompanyApproved || isActiveMember) && !isRegisteredUser;

  const handleDownloadCertificate = async () => {
    if (!currentMemberId || !hasMemberDownloads) return;
    setDownloadingCertificate(true);
    try {
      const companyName =
        companyRegistration?.company_info?.company_name ||
        profileData?.company_name ||
        profileData?.company ||
        profileData?.full_name ||
        profileData?.name ||
        'MEMBER COMPANY';
      const issueDateRaw = profileData?.certificate_issued_at || profileData?.validated_at || new Date().toISOString();
      const issueDate = new Date(issueDateRaw).toLocaleDateString('en-GB');
      const params = new URLSearchParams({
        memberName: companyName,
        issueDate,
        autodownload: '1',
        closeAfter: '1',
      });
      const templateUrl = `${window.location.origin}/download-templates/verified_member_editable.html?${params.toString()}`;
      const popup = window.open(templateUrl, '_blank');
      if (!popup) {
        window.location.href = templateUrl;
      }
      toast.success('Certificate PNG download started');
    } catch (err) {
      toast.error(err?.message || 'Failed to download certificate');
    } finally {
      setDownloadingCertificate(false);
    }
  };

  const handleDownloadBadge = async () => {
    try {
      if (!hasMemberDownloads) {
        toast.error('Company must be approved to download badge');
        return;
      }
      setDownloadingBadge(true);
      const companyName =
        companyRegistration?.company_info?.company_name ||
        profileData?.company_name ||
        profileData?.company ||
        'COMPANY NAME';
      const params = new URLSearchParams({
        companyName,
        memberStatus: 'VERIFIED MEMBER',
        autodownload: '1',
        closeAfter: '1',
      });
      const templateUrl = `${window.location.origin}/download-templates/verified_member_badge_editable.html?${params.toString()}`;
      const popup = window.open(templateUrl, '_blank');
      if (!popup) {
        window.location.href = templateUrl;
      }
      toast.success('Badge PNG download started');
    } catch (error) {
      console.error('Failed to download badge:', error);
      toast.error('Failed to download badge. Please try again.');
    } finally {
      setDownloadingBadge(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center justify-center gap-1 leading-none">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={16}
            className={`shrink-0 ${star <= Math.round(rating || 0) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`}
          />
        ))}
        <span className="ml-2 inline-flex items-center text-sm leading-none text-gray-600">({(rating || 0).toFixed(1)})</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading profile...</span>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Failed to load profile</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <h1 className="text-3xl font-bold text-gray-900">My Profile</h1>
          <p className="text-gray-600 mt-1">Manage your account information</p>
        </div>
        <div className="flex w-full flex-wrap gap-3 md:flex-1 md:justify-end">
          {!isEditing ? (
            <Button onClick={() => setIsEditing(true)} className="bg-blue-600 hover:bg-blue-700 text-white">
              <Edit2 size={16} className="mr-2" />
              Edit Profile
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700 text-white">
                <Save size={16} className="mr-2" />
                Save Changes
              </Button>
            </>
          )}
          <Button variant="outline" onClick={() => setShowChangePasswordDialog(true)}>
            <Lock size={16} className="mr-2" />
            Change Password
          </Button>
          {hasMemberDownloads && (
            <Button
              variant="outline"
              onClick={handleDownloadCertificate}
              disabled={downloadingCertificate}
              className="border-blue-200 text-blue-700 hover:bg-blue-50"
            >
              <Download size={16} className="mr-2" />
              {downloadingCertificate ? 'Downloading...' : 'Download Certificate'}
            </Button>
          )}
          {hasMemberDownloads && (
            <Button
              variant="outline"
              onClick={handleDownloadBadge}
              disabled={downloadingBadge}
              className="border-blue-200 text-blue-700 hover:bg-blue-50"
            >
              <Download size={16} className="mr-2" />
              {downloadingBadge ? 'Preparing Badge...' : 'Download Badge'}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <div className="lg:col-span-1 space-y-6">
          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                <div className="relative inline-flex flex-col items-center">
                  <img
                    src={getProfilePictureOrInitials(profileData, profileData.individual_name)}
                    alt={profileData.individual_name || 'Member profile'}
                    className="w-32 h-32 rounded-full mx-auto mb-4 border-4 border-blue-100 object-cover"
                    onError={(e) => { e.target.src = `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(profileData.individual_name || profileData.company_name)}`; }}
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleShareClick}
                    className="mt-3 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 px-4 text-white shadow-md transition-all hover:from-blue-700 hover:to-indigo-700 hover:shadow-lg lg:absolute lg:left-full lg:top-1/2 lg:ml-3 lg:mt-0 lg:-translate-y-1/2"
                  >
                    <Share2 size={14} className="mr-2" />
                    Share
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => setShowProfilePictureDialog(true)}
                  >
                    <Camera size={14} className="mr-2" />
                    {profileData.profile_picture ? 'Change Profile Picture' : 'Upload Profile Picture'}
                  </Button>
                </div>
                <h2 className="mt-5 break-words text-xl font-bold text-gray-900">{profileData.individual_name}</h2>
                <p className="mt-1 break-words text-sm text-gray-600">{profileData.designation || 'Member'}</p>
                <p className="break-words text-sm text-gray-500">{profileData.company_name}</p>

                <Badge className={`mt-3 ${getTierColor(profileData.tier).badge}`}>
                  {profileData.tier} Member
                </Badge>

                <div className="mt-3">
                  {renderStars(effectiveMemberRating)}
                </div>

                <p className="text-sm text-gray-500 mt-3">
                  <Calendar size={14} className="inline mr-1" />
                  Member since {new Date(profileData.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </p>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="space-y-3">
                  <div className="flex items-start text-sm text-gray-600">
                    <MapPin size={16} className="mr-2 mt-0.5 shrink-0 text-gray-400" />
                    <span className="min-w-0 break-words">{profileData.city}, {profileData.country}</span>
                  </div>
                  <div className="flex items-start text-sm text-gray-600">
                    <Mail size={16} className="mr-2 mt-0.5 shrink-0 text-gray-400" />
                    <span className="min-w-0 break-all">{profileData.email}</span>
                  </div>
                  <div className="flex items-start text-sm text-gray-600">
                    <Phone size={16} className="mr-2 mt-0.5 shrink-0 text-gray-400" />
                    <span className="min-w-0 break-words">{profileData.phone || 'Not provided'}</span>
                  </div>
                  {profileData.whatsapp && (
                    <div className="flex items-start text-sm text-gray-600">
                      <Phone size={16} className="mr-2 mt-0.5 shrink-0 text-green-500" />
                      <span className="min-w-0 break-words">WhatsApp: {profileData.whatsapp}</span>
                    </div>
                  )}
                  {profileData.linkedin && (
                    <div className="flex items-start text-sm text-gray-600">
                      <Linkedin size={16} className="mr-2 mt-0.5 shrink-0 text-blue-600" />
                      <a href={profileData.linkedin} target="_blank" rel="noopener noreferrer" className="min-w-0 break-all text-blue-600 hover:underline">
                        LinkedIn Profile
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Member ID Card */}
          <Card className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <Building2 size={24} />
                <img
                  src="/images/one-globe-company-logo.svg"
                  alt="One Globe Alliance"
                  className="h-14 max-w-[220px] rounded bg-white/95 px-3 py-2 object-contain"
                />
              </div>
              <p className="text-lg font-bold">{profileData.member_id}</p>
              <p className="text-sm opacity-75">Member ID</p>
              <div className="mt-4 pt-4 border-t border-white/20">
                <p className="text-sm font-medium">{profileData.company_id}</p>
                <p className="text-xs opacity-75">Company ID</p>
              </div>
              {hasMemberDownloads && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadCertificate}
                  disabled={downloadingCertificate}
                  className="mt-4 w-full border-white/40 text-white hover:bg-white/20"
                >
                  <Download size={14} className="mr-2" />
                  {downloadingCertificate ? 'Downloading...' : 'Download Certificate'}
                </Button>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {isEditing && canManageCompanyDocuments ? (
            <Card>
              <CardHeader>
                <CardTitle>Company Logo</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-4 text-sm text-gray-600">
                  This logo represents your company and is separate from your personal profile picture.
                </p>
                <CompanyLogoEditor
                  profileData={profileData}
                  uploadingLogo={uploadingLogo}
                  onFileSelect={handleLogoUpload}
                  fileInputRef={logoInputRef}
                />
              </CardContent>
            </Card>
          ) : null}
          <Card>
            <CardHeader>
              <CardTitle>Account Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={profileData.individual_name || ''}
                    onChange={(e) => setProfileData({ ...profileData, individual_name: e.target.value })}
                    disabled={!isEditing}
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={profileData.email || ''}
                    disabled={true}
                    className="bg-gray-50"
                  />
                  <p className="text-xs text-gray-500 mt-1">Contact admin to change email</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="company">Company Name</Label>
                  <Input
                    id="company"
                    value={profileData.company_name || ''}
                    disabled={true}
                    className="bg-gray-50"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    value={profileData.phone || ''}
                    onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                    disabled={!isEditing}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="whatsapp">WhatsApp</Label>
                  <Input
                    id="whatsapp"
                    value={profileData.whatsapp || ''}
                    onChange={(e) => setProfileData({ ...profileData, whatsapp: e.target.value })}
                    disabled={!isEditing}
                    placeholder="+1 234 567 8900"
                  />
                </div>
                <div>
                  <Label htmlFor="linkedin">LinkedIn Profile URL</Label>
                  <Input
                    id="linkedin"
                    value={profileData.linkedin || ''}
                    onChange={(e) => setProfileData({ ...profileData, linkedin: e.target.value })}
                    disabled={!isEditing}
                    placeholder="https://linkedin.com/in/..."
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={profileData.city || ''}
                    onChange={(e) => setProfileData({ ...profileData, city: e.target.value })}
                    disabled={!isEditing}
                  />
                </div>
                <div>
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    value={profileData.country || ''}
                    disabled={true}
                    className="bg-gray-50"
                  />
                </div>
              </div>

              <div>
                <Label>Specializations</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {(profileData.specializations || []).map((spec, index) => (
                    <Badge key={index} variant="outline" className="flex items-center gap-1">
                      <span>{spec}</span>
                      {isEditing && canManageSpecializations && (
                        <button
                          type="button"
                          aria-label={`Remove ${spec}`}
                          className="ml-1 text-gray-500 hover:text-red-600"
                          onClick={() => handleRemoveSpecialization(spec)}
                        >
                          <X size={12} />
                        </button>
                      )}
                    </Badge>
                  ))}
                  {isEditing && canManageSpecializations && (
                    <>
                      {!isAddingSpecialization ? (
                        <Button variant="outline" size="sm" className="h-7" onClick={handleStartSpecializationAdd}>
                          + Add
                        </Button>
                      ) : (
                        <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1">
                          <Input
                            value={specializationDraft}
                            onChange={(e) => setSpecializationDraft(e.target.value)}
                            list="specialization-options"
                            placeholder="Select or type specialization"
                            className="h-7 min-w-[220px] border-0 p-0 shadow-none focus-visible:ring-0"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleConfirmSpecializationAdd();
                              }
                              if (e.key === 'Escape') {
                                e.preventDefault();
                                handleDiscardSpecializationAdd();
                              }
                            }}
                            autoFocus
                          />
                          <datalist id="specialization-options">
                            {specializationSuggestions.map((option) => (
                              <option key={option} value={option} />
                            ))}
                          </datalist>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-emerald-600 hover:text-emerald-700"
                            onClick={handleConfirmSpecializationAdd}
                          >
                            <Check size={16} />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-red-500 hover:text-red-600"
                            onClick={handleDiscardSpecializationAdd}
                          >
                            <X size={16} />
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
                {!canManageSpecializations && (
                  <p className="text-xs text-gray-500 mt-2">
                    Specializations are managed centrally by your Company Admin.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Company Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Building2 size={20} className="mr-2 text-blue-600" />
                Company Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Company Name</Label>
                  <p className="font-medium text-gray-900 mt-1">{profileData.company_name || '—'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Company ID</Label>
                  <p className="font-medium text-gray-900 mt-1">{profileData.company_id || '—'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Country</Label>
                  <p className="font-medium text-gray-900 mt-1">{profileData.country || '—'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">City</Label>
                  <p className="font-medium text-gray-900 mt-1">{profileData.city || '—'}</p>
                </div>
              </div>
              {companyRegistration && (
                <div className="flex flex-col gap-6 border-t border-gray-200 pt-4 md:flex-row md:items-end md:justify-between">
                  {companyRegistration && (
                    <div className="space-y-4">
                      <div>
                        <Label className="text-gray-500">Registration Status</Label>
                        <div className="mt-2">
                          {companyRegistration.status?.toLowerCase() === 'approved' ? (
                            <Badge className="bg-green-100 text-green-700">
                              <CheckCircle size={12} className="mr-1" />
                              Approved
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700">
                              <Clock size={12} className="mr-1" />
                              {companyRegistration.status || 'Pending'}
                            </Badge>
                          )}
                        </div>
                      </div>
                      {canManageCompanyDocuments && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate('/my-company-registration')}
                        >
                            <FileText size={14} className="mr-2" />
                            Registered Company Details
                          </Button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Payment Bank Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {bankDetailsLoading ? (
                <p className="text-sm text-gray-500">Loading bank details...</p>
              ) : bankDetails.length === 0 ? (
                <p className="text-sm text-gray-500">No saved bank details found.</p>
              ) : (
                <div className="space-y-3">
                  {bankDetails.map((detail) => (
                    <div key={detail.detail_id} className="rounded-lg border border-gray-200 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-medium text-gray-900">{detail.label || detail.bank_name || 'Bank details'}</p>
                          <p className="text-sm text-gray-600">{detail.account_name} | {detail.bank_name}</p>
                          <p className="text-xs text-gray-500">A/C: {detail.account_number}</p>
                          {(detail.swift_code || detail.iban) && (
                            <p className="text-xs text-gray-500">SWIFT/IBAN: {detail.swift_code || detail.iban}</p>
                          )}
                          {detail.bank_address && <p className="text-xs text-gray-500">Branch: {detail.bank_address}</p>}
                          {(detail.currency || detail.country) && (
                            <p className="text-xs text-gray-500">
                              {[detail.currency, detail.country].filter(Boolean).join(' | ')}
                            </p>
                          )}
                        </div>
                        {canManageBankDetails && (
                          <div className="flex items-center gap-2 shrink-0">
                            <Button type="button" variant="outline" size="sm" onClick={() => startEditBankDetail(detail)}>
                              Edit
                            </Button>
                            <Button type="button" variant="outline" size="sm" className="text-red-600" onClick={() => handleDeleteBankDetail(detail.detail_id)}>
                              <Trash2 size={14} className="mr-1" />
                              Delete
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {canManageBankDetails ? (
                <div className="rounded-lg border border-blue-100 bg-blue-50/40 p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-gray-900">
                      {bankForm.detail_id ? 'Edit Bank Account' : 'Add Bank Account'}
                    </p>
                    {bankForm.detail_id ? (
                      <Button type="button" variant="ghost" size="sm" onClick={resetBankForm}>Cancel Edit</Button>
                    ) : null}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Input value={bankForm.account_name} onChange={(e) => setBankForm({ ...bankForm, account_name: e.target.value })} placeholder="Account Holder Name *" />
                    <Input value={bankForm.bank_name} onChange={(e) => setBankForm({ ...bankForm, bank_name: e.target.value })} placeholder="Bank Name *" />
                    <Input value={bankForm.account_number} onChange={(e) => setBankForm({ ...bankForm, account_number: e.target.value })} placeholder="Account Number *" />
                    <Input value={bankForm.swift_code} onChange={(e) => setBankForm({ ...bankForm, swift_code: e.target.value })} placeholder="SWIFT Code" />
                    <Input value={bankForm.iban} onChange={(e) => setBankForm({ ...bankForm, iban: e.target.value })} placeholder="IBAN" />
                    <Input value={bankForm.bank_address} onChange={(e) => setBankForm({ ...bankForm, bank_address: e.target.value })} placeholder="Branch Address" />
                    <Input value={bankForm.currency} onChange={(e) => setBankForm({ ...bankForm, currency: e.target.value })} placeholder="Currency" />
                    <Input value={bankForm.country} onChange={(e) => setBankForm({ ...bankForm, country: e.target.value })} placeholder="Country" />
                    <Input value={bankForm.label} onChange={(e) => setBankForm({ ...bankForm, label: e.target.value })} placeholder="Label (optional)" />
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={bankForm.is_default}
                        onChange={(e) => setBankForm({ ...bankForm, is_default: e.target.checked })}
                      />
                      Set as default
                    </label>
                  </div>
                  <Button type="button" onClick={handleSaveBankDetail} disabled={bankDetailsSaving} className="bg-blue-600 hover:bg-blue-700 text-white">
                    <Plus size={14} className="mr-1" />
                    {bankDetailsSaving ? 'Saving...' : bankForm.detail_id ? 'Update Bank Detail' : 'Add Bank Detail'}
                  </Button>
                </div>
              ) : (
                <p className="text-xs text-gray-500">Only company admin can manage bank details.</p>
              )}
            </CardContent>
          </Card>

          {/* Statistics */}
          <Card>
            <CardHeader>
              <CardTitle>Account Statistics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {pointsSystemEnabled && (
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-3xl font-bold text-blue-600">{stats?.reward_points?.toLocaleString() || 0}</p>
                    <p className="text-sm text-gray-600 mt-1">Reward Points</p>
                  </div>
                )}
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-3xl font-bold text-green-600">{stats?.total_quotes || 0}</p>
                  <p className="text-sm text-gray-600 mt-1">Total Quotes</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <p className="text-3xl font-bold text-purple-600">{stats?.active_deals ?? stats?.deals_won ?? 0}</p>
                  <p className="text-sm text-gray-600 mt-1">Active Deals</p>
                  <p className="text-xs text-gray-500 mt-0.5">Nominated to me + by me</p>
                </div>
                <div className="text-center p-4 bg-amber-50 rounded-lg">
                  <p className="text-xl md:text-2xl font-bold text-amber-600 leading-tight break-words">{stats?.response_time_display || stats?.response_time || '12 hours'}</p>
                  <p className="text-sm text-gray-600 mt-1">Response Time</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Additional Info */}
          <Card>
            <CardHeader>
              <CardTitle>Performance Highlights</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="flex items-center p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg">
                  <Award size={40} className="text-blue-600 mr-4" />
                  <div>
                    <p className="font-semibold text-gray-900">{stats?.win_ratio ?? stats?.win_rate ?? 0}%</p>
                    <p className="text-sm text-gray-600">Win Ratio</p>
                    <p className="text-xs text-gray-500">Nominated / received</p>
                  </div>
                </div>
                <div className="flex items-center p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg">
                  <Briefcase size={40} className="text-green-600 mr-4" />
                  <div>
                    <p className="font-semibold text-gray-900">{stats?.completed_deals || 0}</p>
                    <p className="text-sm text-gray-600">Completed Deals</p>
                  </div>
                </div>
                <div className="flex items-center p-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg">
                  <DollarSign size={40} className="text-emerald-600 mr-4" />
                  <div>
                    <p className="font-semibold text-gray-900">
                      ${(stats?.revenue ?? 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    </p>
                    <p className="text-sm text-gray-600">Revenue</p>
                    <p className="text-xs text-gray-500">From completed deals</p>
                  </div>
                </div>
                <div className="flex items-center p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg">
                  <Star size={40} className="text-purple-600 mr-4" />
                  <div>
                    <p className="font-semibold text-gray-900">{effectiveMemberRating.toFixed(1)}</p>
                    <p className="text-sm text-gray-600">Member Rating</p>
                    <p className="text-xs text-gray-500">{stats?.reviews_count ?? 0} reviews</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      <ProfilePictureUploadDialog
        open={showProfilePictureDialog}
        onOpenChange={setShowProfilePictureDialog}
        onUploaded={(profilePicture) => {
          setProfileData((current) => ({ ...current, profile_picture: profilePicture }));
          setOriginalData((current) => ({ ...current, profile_picture: profilePicture }));
        }}
      />

      {/* Change Password Dialog */}
      <Dialog open={showChangePasswordDialog} onOpenChange={handleChangePasswordDialogClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="current-password">Current Password</Label>
              <Input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                disabled={changingPassword}
              />
            </div>
            <div>
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 8 characters)"
                disabled={changingPassword}
              />
            </div>
            <div>
              <Label htmlFor="confirm-password">Confirm New Password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                disabled={changingPassword}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => handleChangePasswordDialogClose(false)} disabled={changingPassword}>
              Cancel
            </Button>
            <Button onClick={handleChangePasswordSubmit} disabled={changingPassword} className="bg-blue-600 hover:bg-blue-700 text-white">
              {changingPassword ? 'Changing...' : 'Change Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Profile;
